import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTheme } from 'styled-components';
import Badge from '../../../../common/base/Badge';
import Button, { ClickableArea } from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Icon from '../../../../common/base/Icon';
import Tabs from '../../../../common/base/Tabs';
import Text from '../../../../common/base/Text';
import CopyableField from '../../../../common/CopyableField';
import { Div } from '../../../../common/helpers/StyledUtils';
import { CLIOnboardingContext } from '../GuidedCLIOnboarding';
interface Props {
  context: CLIOnboardingContext;
  mutateContext: (input: any) => void;
  nextStep: () => void;
  is_current_step: boolean;
}
const server_setup_guides = {
  nodejs: {
    label: 'Node.js',
    commands: ['git clone https://github.com/hookdeck/quickstarts.git && cd quickstarts/typescript/inbound', 'npm install', 'npm run dev'],
    output: `> node-js@1.0.0 dev
> nodemon index.ts

[nodemon] 3.0.1
[nodemon] to restart at any time, enter \`rs\`
[nodemon] watching path(s): *.*
[nodemon] watching extensions: ts,json
[nodemon] starting \`ts-node index.ts\`
🪝 Server running at http://localhost:3000`
  },
  python: {
    label: 'Python',
    commands: ['git clone https://github.com/hookdeck/quickstarts.git && cd quickstarts/python/inbound', 'virtualenv venv && source venv/bin/activate', 'pip install -r requirements.txt', 'python app.py'],
    output: `* Serving Flask app 'app.py'
* Debug mode: on
* Running on http://127.0.0.1:5000`
  },
  go: {
    label: 'Go',
    commands: ['git clone https://github.com/hookdeck/quickstarts.git && cd quickstarts/go/inbound', 'go run main.go'],
    output: `🪝 Server running at http://localhost:3030`
  },
  dotnet: {
    label: 'C#',
    commands: ['git clone https://github.com/hookdeck/quickstarts.git && cd quickstarts/dotnet/inbound', 'dotnet run'],
    output: `Building...
🪝 Server running at http://localhost:5000`
  }
};
type ServerGuideKey = keyof typeof server_setup_guides;
const LocalServerSetup: React.FC<Props> = () => {
  const $ = _c(30);
  const [is_expanded, setIsExpanded] = useState(false);
  const [active_tab, setActiveTab] = useState("nodejs");
  const [show_output, setShowOutput] = useState(false);
  useTheme();
  const current_guide = server_setup_guides[active_tab];
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      direction: "column",
      gap: 4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = is_expanded ? 0 : 3;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      x: 3,
      y: 3,
      b: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      align: "center",
      justify: "space-between"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      align: "center",
      gap: 2
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = is_expanded ? "expand_less" : "expand_more";
  let t6;
  if ($[5] !== is_expanded) {
    t6 = () => setIsExpanded(!is_expanded);
    $[5] = is_expanded;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5 || $[8] !== t6) {
    t7 = <Div flex={t3}><Text semi={true} flex={t4}><Button small={true} icon={t5} ghost={true} onClick={t6} />Local development server setup guide</Text></Div>;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== active_tab || $[11] !== is_expanded) {
    t8 = is_expanded && <Div><Tabs tabs={Object.entries(server_setup_guides).map(_temp)} active_tab={active_tab} onTabSelected={key_0 => setActiveTab(key_0 as ServerGuideKey)} compact={true} border={false} m={{
        t: 2,
        x: 0
      }} /></Div>;
    $[10] = active_tab;
    $[11] = is_expanded;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t2 || $[14] !== t7 || $[15] !== t8) {
    t9 = <StyledCardSection p={t2}>{t7}{t8}</StyledCardSection>;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== current_guide || $[18] !== is_expanded) {
    t10 = is_expanded && <StyledCardSection><Div p={2} flex={{
        direction: "column",
        gap: 2
      }}>{current_guide.commands.map(_temp2)}</Div></StyledCardSection>;
    $[17] = current_guide;
    $[18] = is_expanded;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <StyledCard overflow_hidden={true}>{t9}{t10}</StyledCard>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== current_guide || $[24] !== is_expanded || $[25] !== show_output) {
    t12 = is_expanded && <Div><ClickableArea p={{
        y: 0,
        x: 1
      }} m={{
        l: -1
      }} rounded={true} style={{
        width: "auto"
      }} flex={{
        justify: "flex-start",
        align: "center",
        gap: 1
      }} onClick={() => setShowOutput(!show_output)}><Text size="xs" semi={true} muted={!show_output}>View intended output</Text><Icon muted={true} icon={show_output ? "arrow_drop_down" : "arrow_right"} /></ClickableArea>{show_output && <StyledCard m={{
        t: 2
      }} overflow_hidden={true} borderless={false} style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-all"
      }}><StyledCardSection muted={true} p={3}><Text mono={true} size="s">{current_guide.output}</Text></StyledCardSection></StyledCard>}</Div>;
    $[23] = current_guide;
    $[24] = is_expanded;
    $[25] = show_output;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = <Div flex={t0}>{t11}{t12}</Div>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
export default LocalServerSetup;
function _temp(t0) {
  const [key, guide] = t0;
  return {
    key,
    label: guide.label
  };
}
function _temp2(cmd, index) {
  return <CopyableField key={cmd} m={0} p={{
    x: 3,
    y: 3
  }} value={cmd} wrap={true} variant="command_list" valueElement={<Div flex={{
    gap: 2
  }}><Badge neutral={true}>{index + 1}</Badge><Text mono={true} align="left" text_wrap={false} white_space="pre-wrap" style={{
      minWidth: 0
    }}>{cmd}</Text></Div>} mono={true} />;
}